import {TranslateService} from '@ngx-translate/core';
import {TechPortalTextTemplateContextConfig} from '@app/tech-portal/features/templates';
import {
  MissionByTaskDto,
  MissionByTicketDto,
  MissionDescriptionContextDto,
  MissionDto,
  TemplateContextType
} from '@app/core-shared';

export function provideTextTemplateContextConfig(translateService: TranslateService): TechPortalTextTemplateContextConfig {
  return {
    /* Missions */
    [TemplateContextType.MissionDescription]: {
      serializedName: 'MissionExtendedTtm',
      prefix: 'mission',
      sanitize: (mission: MissionDto): MissionDescriptionContextDto => {
        if (!mission) return {};

        return {
          missionId: mission.missionId,
          state: mission.state,
          actualEnd: mission.actualEnd,
          actualStart: mission.actualStart,
          plannedEnd: mission.plannedEnd,
          plannedStart: mission.plannedStart,
          stereotypeId: mission.stereotypeId,
          tenantId: mission.tenantId,
          locationId: mission.location?.locationId,
          resourceId: mission.resource?.resourceId,
          solutionContactId: mission.solutionContact?.contactId,
          shippingAddressId: mission.shippingAddress?.addressId,
          invoiceAddressId: mission.invoiceAddress?.addressId,
          reportId: mission.reportId,
          title: mission.title
        };
      }
    },
    [TemplateContextType.MissionByTicketDescription]: {
      serializedName: 'MissionExtendedByTicketTtm',
      prefix: 'mission',
      sanitize: (mission: MissionByTicketDto): MissionDescriptionContextDto => {
        if (!mission) return {};

        return {
          missionId: mission.missionId,
          state: mission.state,
          actualEnd: mission.actualEnd,
          actualStart: mission.actualStart,
          plannedEnd: mission.plannedEnd,
          plannedStart: mission.plannedStart,
          stereotypeId: mission.stereotypeId,
          tenantId: mission.tenantId,
          ticketId: mission.ticket?.ticketId,
          locationId: mission.location?.locationId,
          resourceId: mission.resource?.resourceId,
          solutionContactId: mission.solutionContact?.contactId,
          shippingAddressId: mission.shippingAddress?.addressId,
          invoiceAddressId: mission.invoiceAddress?.addressId,
          reportId: mission.reportId,
          title: mission.title
        };
      },
      parents: [TemplateContextType.MissionDescription]
    },
    [TemplateContextType.MissionByTaskDescription]: {
      serializedName: 'MissionExtendedTtm',
      prefix: 'mission',
      sanitize: (mission: MissionByTaskDto): MissionDescriptionContextDto => {
        if (!mission) return {};

        return {
          missionId: mission.missionId,
          state: mission.state,
          actualEnd: mission.actualEnd,
          actualStart: mission.actualStart,
          plannedEnd: mission.plannedEnd,
          plannedStart: mission.plannedStart,
          stereotypeId: mission.stereotypeId,
          tenantId: mission.tenantId,
          taskId: mission.task?.resourceTaskId,
          locationId: mission.location?.locationId,
          resourceId: mission.resource?.resourceId,
          solutionContactId: mission.solutionContact?.contactId,
          shippingAddressId: mission.shippingAddress?.addressId,
          invoiceAddressId: mission.invoiceAddress?.addressId,
          reportId: mission.reportId,
          title: mission.title
        };
      },
      parents: [TemplateContextType.MissionDescription]
    },

    /* Notes */
    [TemplateContextType.NoteBase]: {
      serializedName: 'TextTemplateTtm',
      prefix: 'note',
      sanitize: undefined
    },
    [TemplateContextType.NoteForTicket]: {
      serializedName: 'TicketExtendedTtm',
      prefix: 'ticket',
      sanitize: undefined,
      parents: [TemplateContextType.NoteBase]
    },
    [TemplateContextType.NoteForMission]: {
      serializedName: 'MissionExtendedTtm',
      prefix: 'mission',
      sanitize: undefined,
      parents: [TemplateContextType.NoteBase]
    },
    [TemplateContextType.NoteForMissionByTicket]: {
      serializedName: 'MissionExtendedByTicketTtm',
      prefix: 'mission',
      sanitize: undefined,
      parents: [TemplateContextType.NoteBase, TemplateContextType.NoteForMission]
    },
    [TemplateContextType.NoteForMissionByTask]: {
      serializedName: 'MissionExtendedByTaskTtm',
      prefix: 'mission',
      sanitize: undefined,
      parents: [TemplateContextType.NoteBase, TemplateContextType.NoteForMission]
    },
    [TemplateContextType.InfoNotificationContent]: {
      serializedName: 'InfoExtendedTtm',
      prefix: 'info',
      sanitize: undefined
    },
    [TemplateContextType.InfoNotificationSubject]: {
      serializedName: 'InfoExtendedTtm',
      prefix: 'info',
      sanitize: undefined
    },
    [TemplateContextType.MissionNotificationContent]: {
      serializedName: 'MissionExtendedTtm',
      prefix: 'mission',
      sanitize: undefined
    },
    [TemplateContextType.MissionNotificationSubject]: {
      serializedName: 'MissionExtendedTtm',
      prefix: 'mission',
      sanitize: undefined
    },
    [TemplateContextType.TicketNotificationContent]: {
      serializedName: 'TicketExtendedTtm',
      prefix: 'ticket',
      sanitize: undefined
    },
    [TemplateContextType.TicketNotificationSubject]: {
      serializedName: 'TicketExtendedTtm',
      prefix: 'ticket',
      sanitize: undefined
    },
    [TemplateContextType.IssueEmailSubject]: {
      serializedName: 'IssueTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.IssueEmailContent]: {
      serializedName: 'IssueTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.MessageIssueEmailSubject]: {
      serializedName: 'UserIssueEmailTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.MessageIssueEmailContent]: {
      serializedName: 'UserIssueEmailTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.MissionIssueEmailSubject]: {
      serializedName: 'UserIssueEmailTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.MissionIssueEmailContent]: {
      serializedName: 'UserIssueEmailTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.TicketIssueEmailSubject]: {
      serializedName: 'UserIssueEmailTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.TicketIssueEmailContent]: {
      serializedName: 'UserIssueEmailTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.IssueTitle]: {
      serializedName: 'IssueTtm',
      prefix: 'issue',
      sanitize: undefined
    },
    [TemplateContextType.CustomContainerRowTitle]: {
      serializedName: 'ChildStereotypedEntityTtm',
      prefix: 'container',
      sanitize: undefined
    },
  };
}
